import { useId } from 'react'

// ─── Character metadata ───────────────────────────────────────────
const CHARACTERS = {
  nature:  { name: 'Lysa',       color: '#4ade80' },
  space:   { name: 'Pixel',      color: '#818cf8' },
  pirates: { name: 'Kapten Kex', color: '#f59e0b' },
  fantasy: { name: 'Gnista',     color: '#c084fc' },
  default: { name: 'Guiden',     color: '#38bdf8' },
}

const SIZES = { sm: 64, md: 96, lg: 128 }

// All keyframe definitions — injected once by the main export component
const GUIDE_KEYFRAMES = `
  /* ── Lysa (nature wisp) ── */
  @keyframes lysa-float {
    0%, 100% { transform: translateY(0px); }
    50%       { transform: translateY(-8px); }
  }
  @keyframes lysa-glow {
    0%, 100% { opacity: 0.10; transform: scale(1); }
    50%       { opacity: 0.32; transform: scale(1.12); }
  }
  @keyframes lysa-sparkle {
    0%, 100% { opacity: 0; transform: scale(0.3) rotate(0deg); }
    45%, 55% { opacity: 1;  transform: scale(1)   rotate(30deg); }
  }

  /* ── Pixel (space robot) ── */
  @keyframes pixel-hover {
    0%, 100% { transform: translateY(0px); }
    50%       { transform: translateY(-6px); }
  }
  @keyframes pixel-blink {
    0%, 88%, 100% { transform: scaleY(1);   opacity: 1;   }
    94%            { transform: scaleY(0.08); opacity: 0.2; }
  }
  @keyframes pixel-antenna {
    0%, 100% { opacity: 0.5; transform: scale(0.85); }
    50%       { opacity: 1;   transform: scale(1.2);  }
  }
  @keyframes pixel-scan {
    0%   { transform: translateX(-14px); opacity: 0;   }
    25%  { opacity: 0.55; }
    75%  { opacity: 0.55; }
    100% { transform: translateX(14px);  opacity: 0;   }
  }

  /* ── Kapten Kex (parrot) ── */
  @keyframes kex-bob {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    30%       { transform: translateY(-6px) rotate(-4deg); }
    65%       { transform: translateY(-3px) rotate(3deg); }
  }
  @keyframes kex-wing-l {
    0%, 100% { transform: rotate(0deg);   }
    50%       { transform: rotate(-22deg); }
  }
  @keyframes kex-wing-r {
    0%, 100% { transform: rotate(0deg);  }
    50%       { transform: rotate(22deg); }
  }

  /* ── Gnista (fantasy dragon) ── */
  @keyframes gnista-float {
    0%, 100% { transform: translateY(0px); }
    50%       { transform: translateY(-7px); }
  }
  @keyframes gnista-tail {
    0%, 100% { transform: rotate(0deg);   }
    40%       { transform: rotate(-20deg); }
    70%       { transform: rotate(14deg);  }
  }
  @keyframes gnista-smoke {
    0%   { opacity: 0;    transform: translate(0, 0)    scale(0.5); }
    30%  { opacity: 0.55; }
    100% { opacity: 0;    transform: translate(0, -18px) scale(1.5); }
  }

  /* ── Guiden (compass star) ── */
  @keyframes guiden-spin {
    from { transform: rotate(0deg);   }
    to   { transform: rotate(360deg); }
  }
  @keyframes guiden-counter {
    from { transform: rotate(0deg);    }
    to   { transform: rotate(-360deg); }
  }
  @keyframes guiden-glow {
    0%, 100% { opacity: 0.12; transform: scale(1);    }
    50%       { opacity: 0.38; transform: scale(1.10); }
  }
  @keyframes guiden-center {
    0%, 100% { transform: scale(1);    opacity: 0.7; }
    50%       { transform: scale(1.25); opacity: 1;   }
  }
`

// ─── Speech bubble ────────────────────────────────────────────────
function SpeechBubble({ message }) {
  return (
    <div className="relative mb-2 max-w-[200px]">
      <div className="rounded-2xl bg-white px-3 py-2 text-sm leading-snug text-slate-700 shadow-lg ring-1 ring-slate-200/60 dark:bg-slate-700 dark:text-white/90 dark:ring-white/10">
        {message}
      </div>
      {/* Triangle pointer pointing down toward the character */}
      <div
        className="absolute -bottom-[9px] left-6 h-[10px] w-[16px] bg-white dark:bg-slate-700"
        style={{ clipPath: 'polygon(0 0, 100% 0, 50% 100%)' }}
        aria-hidden="true"
      />
    </div>
  )
}

// ─── Lysa — forest spirit / glowing wisp ─────────────────────────
function LysaCharacter({ px }) {
  const uid = useId().replace(/:/g, '_')
  const gId = `lysa_g_${uid}`
  return (
    <svg width={px} height={px} viewBox="0 0 100 100" fill="none" aria-hidden="true">
      <defs>
        <radialGradient id={gId} cx="40%" cy="35%" r="65%">
          <stop offset="0%"   stopColor="#86efac" />
          <stop offset="100%" stopColor="#15803d" />
        </radialGradient>
      </defs>

      {/* Pulsing outer glow aura */}
      <circle
        cx="50" cy="50" r="34" fill="#4ade80"
        style={{ transformBox: 'fill-box', transformOrigin: 'center', animation: 'lysa-glow 2.4s ease-in-out infinite' }}
      />
      {/* Mid glow ring */}
      <circle cx="50" cy="50" r="23" fill="#4ade80" opacity="0.18" />

      {/* Floating body group */}
      <g style={{ animation: 'lysa-float 2.8s ease-in-out infinite' }}>
        {/* Wisp tail */}
        <path
          d="M43 65 Q38 76 36 84 Q42 77 50 81 Q58 77 64 84 Q62 76 57 65 Q53 71 50 71 Q47 71 43 65Z"
          fill="#22c55e" opacity="0.62"
        />
        {/* Main orb */}
        <circle cx="50" cy="50" r="17" fill={`url(#${gId})`} />
        {/* Gleam highlight */}
        <ellipse cx="43" cy="43" rx="5.5" ry="4.5" fill="white" opacity="0.48" />
        <circle  cx="41.5" cy="41.5" r="2.2" fill="white" opacity="0.72" />
        {/* Eyes */}
        <circle cx="45" cy="50" r="3"   fill="#052e16" />
        <circle cx="55" cy="50" r="3"   fill="#052e16" />
        <circle cx="46" cy="49" r="1.1" fill="white" />
        <circle cx="56" cy="49" r="1.1" fill="white" />
        {/* Smile */}
        <path d="M46 56 Q50 60.5 54 56" stroke="#052e16" strokeWidth="1.6" strokeLinecap="round" fill="none" />
        {/* Blush */}
        <ellipse cx="42" cy="54.5" rx="3.5" ry="2"   fill="#f472b6" opacity="0.28" />
        <ellipse cx="58" cy="54.5" rx="3.5" ry="2"   fill="#f472b6" opacity="0.28" />
      </g>

      {/* Orbiting sparkle stars */}
      <g style={{ transformBox: 'fill-box', transformOrigin: '26px 37px', animation: 'lysa-sparkle 2.4s ease-in-out infinite 0.3s' }}>
        <path d="M26 37 L27.6 32 L29.2 37 L34 38.5 L29.2 40 L27.6 45 L26 40 L21 38.5Z" fill="#86efac" />
      </g>
      <g style={{ transformBox: 'fill-box', transformOrigin: '73px 43px', animation: 'lysa-sparkle 2.4s ease-in-out infinite 1.1s' }}>
        <path d="M73 43 L74.6 38 L76.2 43 L81 44.5 L76.2 46 L74.6 51 L73 46 L68 44.5Z" fill="#86efac" />
      </g>
      <g style={{ transformBox: 'fill-box', transformOrigin: '77px 22px', animation: 'lysa-sparkle 2s ease-in-out infinite 0.7s' }}>
        <circle cx="77" cy="22" r="3"   fill="#bbf7d0" />
        <circle cx="21" cy="66" r="2.2" fill="#bbf7d0" />
      </g>
    </svg>
  )
}

// ─── Pixel — friendly space robot / hologram ─────────────────────
function PixelCharacter({ px }) {
  const uid  = useId().replace(/:/g, '_')
  const hGid = `px_h_${uid}`
  const bGid = `px_b_${uid}`
  return (
    <svg width={px} height={px} viewBox="0 0 100 100" fill="none" aria-hidden="true">
      <defs>
        <radialGradient id={hGid} cx="50%" cy="28%" r="60%">
          <stop offset="0%"   stopColor="#4338ca" />
          <stop offset="100%" stopColor="#1e1b4b" />
        </radialGradient>
        <linearGradient id={bGid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#4338ca" />
          <stop offset="100%" stopColor="#1e1b4b" />
        </linearGradient>
      </defs>

      {/* Hover shadow */}
      <ellipse
        cx="50" cy="90" rx="18" ry="4.5" fill="#818cf8" opacity="0.18"
        style={{ animation: 'pixel-hover 3s ease-in-out infinite', animationDirection: 'reverse' }}
      />

      {/* Whole character hover float */}
      <g style={{ animation: 'pixel-hover 3s ease-in-out infinite' }}>

        {/* ── Body ── */}
        <rect x="33" y="60" width="34" height="22" rx="6" fill={`url(#${bGid})`} />
        <rect x="33" y="60" width="34" height="22" rx="6" stroke="#6366f1" strokeWidth="1" fill="none" opacity="0.5" />
        {/* Body vents */}
        <rect x="37" y="67" width="6" height="3" rx="1.5" fill="#6366f1" opacity="0.65" />
        <rect x="47" y="67" width="6" height="3" rx="1.5" fill="#6366f1" opacity="0.65" />
        <rect x="57" y="67" width="6" height="3" rx="1.5" fill="#6366f1" opacity="0.65" />
        {/* Chest power LED */}
        <circle cx="50" cy="74" r="4.5" fill="#a5f3fc" opacity="0.8" />
        <circle cx="50" cy="74" r="2.5" fill="white" />

        {/* ── Arms ── */}
        <rect x="21" y="62" width="10" height="15" rx="5" fill="#312e81" />
        <rect x="69" y="62" width="10" height="15" rx="5" fill="#312e81" />

        {/* ── Head ── */}
        <rect x="28" y="22" width="44" height="38" rx="11" fill={`url(#${hGid})`} />
        <rect x="28" y="22" width="44" height="38" rx="11" stroke="#6366f1" strokeWidth="1.5" fill="none" />

        {/* Screen visor */}
        <rect x="32" y="27" width="36" height="28" rx="8" fill="#080520" />

        {/* Scan line */}
        <rect
          x="32" y="37" width="36" height="2" rx="1" fill="#818cf8" opacity="0.5"
          style={{ animation: 'pixel-scan 2.5s ease-in-out infinite' }}
        />

        {/* Main LED eye — blinks */}
        <g style={{ transformBox: 'fill-box', transformOrigin: 'center', animation: 'pixel-blink 3.8s ease-in-out infinite 1s' }}>
          <circle cx="50" cy="40" r="9"   fill="#0f0a3e" />
          <circle cx="50" cy="40" r="7"   fill="#4f46e5" opacity="0.4" />
          <circle cx="50" cy="40" r="5.5" fill="#a5b4fc" />
          <circle cx="50" cy="40" r="3.5" fill="#e0e7ff" />
          <circle cx="48.5" cy="38.5" r="1.3" fill="white" />
        </g>

        {/* Neck connector */}
        <rect x="44" y="58" width="12" height="5" rx="2.5" fill="#312e81" />

        {/* Antenna stem */}
        <line x1="50" y1="22" x2="50" y2="12" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" />
        {/* Antenna tip — pulses */}
        <g style={{ transformBox: 'fill-box', transformOrigin: 'center', animation: 'pixel-antenna 1.8s ease-in-out infinite' }}>
          <circle cx="50" cy="9.5" r="4.5" fill="#a5b4fc" />
          <circle cx="50" cy="9.5" r="2.5" fill="white" />
        </g>
      </g>
    </svg>
  )
}

// ─── Kapten Kex — colorful pirate parrot ─────────────────────────
function KaptenKexCharacter({ px }) {
  const uid  = useId().replace(/:/g, '_')
  const hGid = `kx_h_${uid}`
  const bGid = `kx_b_${uid}`
  return (
    <svg width={px} height={px} viewBox="0 0 100 100" fill="none" aria-hidden="true">
      <defs>
        <radialGradient id={hGid} cx="43%" cy="33%" r="62%">
          <stop offset="0%"   stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#b45309" />
        </radialGradient>
        <linearGradient id={bGid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#d97706" />
        </linearGradient>
      </defs>

      {/* ── Wings (flutter independently) ── */}
      <g style={{ transformBox: 'fill-box', transformOrigin: 'right center', animation: 'kex-wing-l 1.4s ease-in-out infinite' }}>
        <path d="M29 57 Q17 50 15 63 Q17 74 29 72 Q33 66 31 59Z" fill="#16a34a" />
        <path d="M31 59 Q21 54 19 65 Q21 71 31 69Z"               fill="#15803d" opacity="0.7" />
      </g>
      <g style={{ transformBox: 'fill-box', transformOrigin: 'left center', animation: 'kex-wing-r 1.4s ease-in-out infinite 0.15s' }}>
        <path d="M71 57 Q83 50 85 63 Q83 74 71 72 Q67 66 69 59Z" fill="#dc2626" />
        <path d="M69 59 Q79 54 81 65 Q79 71 69 69Z"               fill="#b91c1c" opacity="0.7" />
      </g>

      {/* ── Body ── */}
      <ellipse cx="50" cy="74" rx="19" ry="16" fill={`url(#${bGid})`} />
      {/* Belly */}
      <ellipse cx="50" cy="76" rx="10" ry="9" fill="#fef3c7" opacity="0.75" />
      {/* Feet */}
      <path d="M43 89 Q41 93 38 93 M43 89 Q43 94 40 95 M43 89 Q45 93 43 96" stroke="#d97706" strokeWidth="1.8" strokeLinecap="round" fill="none" />
      <path d="M57 89 Q59 93 62 93 M57 89 Q57 94 60 95 M57 89 Q55 93 57 96" stroke="#d97706" strokeWidth="1.8" strokeLinecap="round" fill="none" />

      {/* ── Head group — bobs ── */}
      <g style={{ transformBox: 'fill-box', transformOrigin: 'center bottom', animation: 'kex-bob 2.6s ease-in-out infinite' }}>
        {/* Neck */}
        <ellipse cx="50" cy="60" rx="10" ry="6" fill="#f59e0b" />

        {/* Crest feathers */}
        <path d="M37 25 Q32 12 37 7  Q40 17 42 22Z" fill="#dc2626" />
        <path d="M50 21 Q48 7  52 4  Q55 13 54 21Z" fill="#f97316" />
        <path d="M63 25 Q68 12 63 7  Q60 17 58 22Z" fill="#facc15" />

        {/* Head */}
        <circle cx="50" cy="40" r="22" fill={`url(#${hGid})`} />

        {/* Eye patch area */}
        <circle cx="57" cy="36" r="10" fill="white" opacity="0.88" />
        {/* Eye */}
        <circle cx="57" cy="36" r="7"  fill="#1c1917" />
        <circle cx="57" cy="36" r="4.5" fill="#0ea5e9" />
        <circle cx="57" cy="36" r="2"  fill="#1c1917" />
        {/* Eye shine */}
        <circle cx="54.5" cy="33.5" r="1.6" fill="white" />

        {/* Beak */}
        <path d="M43 44 Q50 55 57 44 L50 40Z" fill="#d97706" />
        <path d="M43 44 Q50 51 57 44" fill="#b45309" opacity="0.5" />
        <line x1="43" y1="44" x2="57" y2="44" stroke="#92400e" strokeWidth="1" opacity="0.6" />

        {/* Nostril dots */}
        <circle cx="47" cy="42" r="1.2" fill="#92400e" />
        <circle cx="50" cy="43" r="1.2" fill="#92400e" />
      </g>
    </svg>
  )
}

// ─── Gnista — tiny fantasy dragon ────────────────────────────────
function GnistaCharacter({ px }) {
  const uid  = useId().replace(/:/g, '_')
  const hGid = `gn_h_${uid}`
  const bGid = `gn_b_${uid}`
  return (
    <svg width={px} height={px} viewBox="0 0 100 100" fill="none" aria-hidden="true">
      <defs>
        <radialGradient id={hGid} cx="40%" cy="30%" r="65%">
          <stop offset="0%"   stopColor="#c084fc" />
          <stop offset="100%" stopColor="#7e22ce" />
        </radialGradient>
        <linearGradient id={bGid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#a855f7" />
          <stop offset="100%" stopColor="#6b21a8" />
        </linearGradient>
      </defs>

      {/* ── Tail (wags independently) ── */}
      <g style={{ transformBox: 'fill-box', transformOrigin: '60px 68px', animation: 'gnista-tail 1.8s ease-in-out infinite' }}>
        <path d="M60 68 Q70 72 77 67 Q82 62 78 56 Q74 60 68 64Z"   fill="#a855f7" />
        <path d="M77 59 L82 53 L79 61Z"                             fill="#e879f9" />
        <path d="M80 57 L85 52 L82 60Z"                             fill="#d946ef" opacity="0.7" />
      </g>

      {/* ── Wings ── */}
      <g style={{ transformBox: 'fill-box', transformOrigin: 'right center', animation: 'kex-wing-l 2.4s ease-in-out infinite' }}>
        <path d="M26 46 Q16 30 20 17 Q26 27 32 34 Q28 40 28 46Z"   fill="#a855f7" opacity="0.65" />
        <path d="M28 46 Q20 33 24 22 Q28 30 32 38Z"                 fill="#c084fc" opacity="0.45" />
      </g>
      <g style={{ transformBox: 'fill-box', transformOrigin: 'left center', animation: 'kex-wing-r 2.4s ease-in-out infinite 0.3s' }}>
        <path d="M74 46 Q84 30 80 17 Q74 27 68 34 Q72 40 72 46Z"   fill="#a855f7" opacity="0.65" />
        <path d="M72 46 Q80 33 76 22 Q72 30 68 38Z"                 fill="#c084fc" opacity="0.45" />
      </g>

      {/* ── Floating body + head group ── */}
      <g style={{ animation: 'gnista-float 3s ease-in-out infinite' }}>

        {/* Body */}
        <ellipse cx="50" cy="70" rx="18" ry="14" fill={`url(#${bGid})`} />
        {/* Belly */}
        <ellipse cx="50" cy="72" rx="9"  ry="7"  fill="#f3e8ff" opacity="0.45" />
        <ellipse cx="50" cy="76" rx="6"  ry="4.5" fill="#f3e8ff" opacity="0.28" />

        {/* Neck */}
        <ellipse cx="50" cy="55" rx="11" ry="7" fill="#9333ea" />

        {/* Head */}
        <circle cx="50" cy="40" r="19" fill={`url(#${hGid})`} />

        {/* Horns */}
        <path d="M37 26 L32 15 L40 24Z" fill="#e879f9" />
        <path d="M35 25 L30 14 L38 23Z" fill="#d946ef" opacity="0.6" />
        <path d="M63 26 L68 15 L60 24Z" fill="#e879f9" />
        <path d="M65 25 L70 14 L62 23Z" fill="#d946ef" opacity="0.6" />

        {/* Brow ridge */}
        <path d="M35 37 Q42 33 45 37" stroke="#c084fc" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        <path d="M65 37 Q58 33 55 37" stroke="#c084fc" strokeWidth="1.5" fill="none" strokeLinecap="round" />

        {/* Eyes with slit pupils */}
        <ellipse cx="43" cy="41" rx="5.5" ry="5.5" fill="#150829" />
        <ellipse cx="57" cy="41" rx="5.5" ry="5.5" fill="#150829" />
        <ellipse cx="43" cy="41" rx="4"   ry="4.5" fill="#facc15" />
        <ellipse cx="57" cy="41" rx="4"   ry="4.5" fill="#facc15" />
        {/* Slit pupils */}
        <ellipse cx="43" cy="41" rx="1.5" ry="3.5" fill="#150829" />
        <ellipse cx="57" cy="41" rx="1.5" ry="3.5" fill="#150829" />
        {/* Eye shine */}
        <circle cx="41" cy="38.5" r="1.1" fill="white" opacity="0.85" />
        <circle cx="55" cy="38.5" r="1.1" fill="white" opacity="0.85" />

        {/* Nostrils */}
        <circle cx="46.5" cy="51" r="1.5" fill="#6b21a8" />
        <circle cx="53.5" cy="51" r="1.5" fill="#6b21a8" />
        <circle cx="46.5" cy="51" r="0.8" fill="#150829" />
        <circle cx="53.5" cy="51" r="0.8" fill="#150829" />

        {/* Mouth */}
        <path d="M44 55 Q50 59.5 56 55" stroke="#c084fc" strokeWidth="1.5" strokeLinecap="round" fill="none" />
        <path d="M46 55 L46 58 M50 56 L50 59.5 M54 55 L54 58" stroke="white" strokeWidth="1" strokeLinecap="round" />

        {/* Smoke puffs from nostrils */}
        <circle cx="44" cy="48" r="3"   fill="#e9d5ff"
          style={{ transformBox: 'fill-box', transformOrigin: 'center bottom', animation: 'gnista-smoke 2.2s ease-in-out infinite 0.5s', opacity: 0 }} />
        <circle cx="56" cy="48" r="2.5" fill="#e9d5ff"
          style={{ transformBox: 'fill-box', transformOrigin: 'center bottom', animation: 'gnista-smoke 2.2s ease-in-out infinite 1.0s', opacity: 0 }} />
        <circle cx="42" cy="45" r="2"   fill="#e9d5ff"
          style={{ transformBox: 'fill-box', transformOrigin: 'center bottom', animation: 'gnista-smoke 2s   ease-in-out infinite 0.9s', opacity: 0 }} />
        <circle cx="58" cy="45" r="1.8" fill="#e9d5ff"
          style={{ transformBox: 'fill-box', transformOrigin: 'center bottom', animation: 'gnista-smoke 2s   ease-in-out infinite 1.4s', opacity: 0 }} />
      </g>
    </svg>
  )
}

// ─── Guiden — compass rose / navigation star ──────────────────────
function GuidenCharacter({ px }) {
  const uid  = useId().replace(/:/g, '_')
  const cGid = `gd_c_${uid}`
  return (
    <svg width={px} height={px} viewBox="0 0 100 100" fill="none" aria-hidden="true">
      <defs>
        <radialGradient id={cGid} cx="40%" cy="35%" r="65%">
          <stop offset="0%"   stopColor="#0ea5e9" />
          <stop offset="100%" stopColor="#0c4a6e" />
        </radialGradient>
      </defs>

      {/* Outer glow pulse */}
      <circle cx="50" cy="50" r="42" fill="#38bdf8"
        style={{ transformBox: 'fill-box', transformOrigin: 'center', animation: 'guiden-glow 2.6s ease-in-out infinite' }} />

      {/* Outer rotating compass star — 8 points */}
      <g style={{ transformBox: 'fill-box', transformOrigin: 'center', animation: 'guiden-spin 14s linear infinite' }}>
        {/* Cardinal N/S/E/W — tall sharp points */}
        <path d="M50 8  L54 46 L50 50 L46 46Z" fill="#38bdf8" />
        <path d="M92 50 L54 54 L50 50 L54 46Z" fill="#0ea5e9" />
        <path d="M50 92 L46 54 L50 50 L54 54Z" fill="#38bdf8" />
        <path d="M8  50 L46 46 L50 50 L46 54Z" fill="#0ea5e9" />
        {/* Intercardinal NE/SE/SW/NW — shorter diagonal points */}
        <path d="M81 19 L56 46 L50 50 L54 44Z" fill="#7dd3fc" opacity="0.75" />
        <path d="M81 81 L54 56 L50 50 L56 54Z" fill="#7dd3fc" opacity="0.75" />
        <path d="M19 81 L44 54 L50 50 L46 56Z" fill="#7dd3fc" opacity="0.75" />
        <path d="M19 19 L46 46 L50 50 L44 46Z" fill="#7dd3fc" opacity="0.75" />
      </g>

      {/* Inner counter-rotating dashed ring + cardinal dots */}
      <g style={{ transformBox: 'fill-box', transformOrigin: 'center', animation: 'guiden-counter 9s linear infinite' }}>
        <circle cx="50" cy="50" r="26" stroke="#bae6fd" strokeWidth="1" fill="none" strokeDasharray="4 5" opacity="0.7" />
        <circle cx="50" cy="25" r="2.8" fill="#e0f2fe" />
        <circle cx="75" cy="50" r="2.8" fill="#e0f2fe" />
        <circle cx="50" cy="75" r="2.8" fill="#e0f2fe" />
        <circle cx="25" cy="50" r="2.8" fill="#e0f2fe" />
      </g>

      {/* Center circle (static) */}
      <circle cx="50" cy="50" r="13"  fill="#0c4a6e" />
      <circle cx="50" cy="50" r="12"  fill={`url(#${cGid})`} />

      {/* "N" direction marker */}
      <text x="50" y="54.5" textAnchor="middle" fill="white" fontSize="10" fontWeight="700" fontFamily="system-ui,sans-serif">N</text>

      {/* Center pulsing ring */}
      <circle cx="50" cy="50" r="5" fill="#38bdf8" opacity="0.35"
        style={{ transformBox: 'fill-box', transformOrigin: 'center', animation: 'guiden-center 2s ease-in-out infinite' }} />
    </svg>
  )
}

// ─── Character map ────────────────────────────────────────────────
const CHARACTER_MAP = {
  nature:  LysaCharacter,
  space:   PixelCharacter,
  pirates: KaptenKexCharacter,
  fantasy: GnistaCharacter,
  default: GuidenCharacter,
}

// ─── Main export ──────────────────────────────────────────────────
/**
 * GuideCharacter — renders a CSS-animated SVG guide character for a quest theme.
 *
 * @param {string}      theme     - 'nature' | 'space' | 'pirates' | 'fantasy' | 'default'
 * @param {'sm'|'md'|'lg'} size  - character display size (64 / 96 / 128 px)
 * @param {string|null} message  - optional speech bubble message
 * @param {string}      className - extra classes on the wrapper
 */
export default function GuideCharacter({ theme = 'default', size = 'md', message = null, className = '' }) {
  const px = SIZES[size] ?? SIZES.md
  const char = CHARACTERS[theme] ?? CHARACTERS.default
  const CharComponent = CHARACTER_MAP[theme] ?? GuidenCharacter

  return (
    <>
      {/* Keyframe definitions — injected once per page */}
      <style>{GUIDE_KEYFRAMES}</style>

      <div className={`flex flex-col items-center gap-1 ${className}`}>
        {/* Speech bubble (above character) */}
        {message && <SpeechBubble message={message} />}

        {/* Character SVG */}
        <CharComponent px={px} />

        {/* Character name */}
        <span
          className="text-xs font-semibold tracking-wide"
          style={{ color: char.color, textShadow: `0 0 8px ${char.color}55` }}
        >
          {char.name}
        </span>
      </div>
    </>
  )
}
